import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Input } from "antd";
import { Check } from "lucide-react";

interface ColorSwatchPickerProps {
  value?: string | null;
  onChange?: (value: string | null) => void;
  presets: string[];
  /** Adds a "no color" swatch that emits `null`. */
  allowNone?: boolean;
  ariaLabel: string;
}

const HEX_PATTERN = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i;

/** Normalizes hex and `rgb()` colors to `#RRGGBB`; returns null for anything else, including transparent. */
export function normalizeHex(color: string | null | undefined): string | null {
  if (!color) return null;
  const text = color.trim();

  const hexMatch = HEX_PATTERN.exec(text);
  if (hexMatch) {
    let digits = hexMatch[1];
    if (digits.length === 3) {
      digits = digits
        .split("")
        .map((c) => c + c)
        .join("");
    }
    return `#${digits.toUpperCase()}`;
  }

  const rgbMatch = /^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*(?:,\s*([\d.]+)\s*)?\)$/i.exec(text);
  if (rgbMatch) {
    if (rgbMatch[4] !== undefined && Number(rgbMatch[4]) === 0) return null;
    const [r, g, b] = [rgbMatch[1], rgbMatch[2], rgbMatch[3]].map((n) =>
      Math.min(255, Number(n)).toString(16).padStart(2, "0")
    );
    return `#${r}${g}${b}`.toUpperCase();
  }

  return null;
}

function ColorSwatchPicker({ value, onChange, presets, allowNone = false, ariaLabel }: ColorSwatchPickerProps) {
  const current = normalizeHex(value);
  const [draft, setDraft] = useState(current ?? "");
  const [invalid, setInvalid] = useState(false);
  const pickerRef = useRef<HTMLDivElement>(null);
  const customRef = useRef<HTMLDivElement>(null);
  const [customWrapped, setCustomWrapped] = useState(false);

  // The Custom group keeps a leading margin so it is spaced from the last swatch on a shared line;
  // once it wraps to its own line, `customWrapped` shifts it back so it lines up with the swatches.
  useLayoutEffect(() => {
    const picker = pickerRef.current;
    const custom = customRef.current;
    if (!picker || !custom) return undefined;

    const measure = () => {
      const previous = custom.previousElementSibling as HTMLElement | null;
      setCustomWrapped(!!previous && previous.offsetTop !== custom.offsetTop);
    };
    measure();

    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(picker);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    setDraft(current ?? "");
    setInvalid(false);
  }, [current]);

  const commitDraft = () => {
    if (draft.trim() === "") {
      setInvalid(false);
      if (allowNone) onChange?.(null);
      else setDraft(current ?? "");
      return;
    }
    const hex = normalizeHex(draft);
    if (!hex) {
      setInvalid(true);
      return;
    }
    setInvalid(false);
    setDraft(hex);
    onChange?.(hex);
  };

  const normalizedPresets = presets.map((preset) => normalizeHex(preset) ?? preset);

  return (
    <div ref={pickerRef} className="settings_swatch_picker" role="group" aria-label={ariaLabel}>
      {allowNone && (
        <button
          type="button"
          className={`settings_swatch settings_swatch_none ${current === null ? "selected" : ""}`}
          aria-label={`${ariaLabel}: none`}
          aria-pressed={current === null}
          title="No color"
          onClick={() => onChange?.(null)}
        />
      )}
      {normalizedPresets.map((hex) => {
        const selected = current === hex;
        return (
          <button
            key={hex}
            type="button"
            className={`settings_swatch ${selected ? "selected" : ""}`}
            style={{ backgroundColor: hex }}
            aria-label={`${ariaLabel}: ${hex}`}
            aria-pressed={selected}
            title={hex}
            onClick={() => onChange?.(hex)}
          >
            {selected && <Check size={12} strokeWidth={3} className="settings_swatch_check" />}
          </button>
        );
      })}
      <div ref={customRef} className={`settings_swatch_custom ${customWrapped ? "wrapped" : ""}`}>
        <span className="settings_swatch_custom_label ui-text-label">Custom</span>
        <Input
          size="small"
          className={`settings_swatch_custom_input ${invalid ? "invalid" : ""}`}
          value={draft}
          placeholder="#000000"
          maxLength={7}
          status={invalid ? "error" : undefined}
          aria-label={`${ariaLabel} hex value`}
          prefix={
            <span
              className="settings_swatch_custom_dot"
              style={{ backgroundColor: current ?? "transparent" }}
              aria-hidden="true"
            />
          }
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commitDraft}
          onPressEnter={commitDraft}
        />
      </div>
      {invalid && <span className="settings_swatch_custom_error ui-text-meta">Use a hex value like #1F6B3E</span>}
    </div>
  );
}

export default ColorSwatchPicker;
